import { useState } from 'react';

import { Divider, Stack, Typography } from '@mui/material';

import { say } from '@/james-playground/aa-family-claim/claim/asks';
import {
  GRID,
  HELP_KIND,
  MANAGES,
  NEEDED_HINT,
} from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  CardShell,
  gateQuestion,
  Chips,
  Count,
  Toggle,
  useQids,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';
import {
  HELP_KINDS,
  gridReady,
  gridValues,
  initialCells,
  known,
  str,
  type Cells,
} from '@/james-playground/aa-family-claim/claim/cards/values';

/**
 * One care activity, row by row: getting into bed, getting out of bed. Each
 * row offers the kinds of help the form asks about for it, and only those:
 * struggling with it, hands-on help, reminding or encouraging. The wording is
 * the help she needs, whoever gives it, or nobody yet.
 *
 * Where the form counts times a day, switching a kind on brings up the count
 * for it. Where the form takes a tick, the switch is the answer.
 *
 * Built to wrap: each row is its label over a wrapping run of chips, and a
 * count sits under the chip it belongs to, so nothing is laid out in columns
 * that a 320px screen or 200% text would crush.
 */
const HelpGridCard = ({
  ask,
  facts,
  voice: v,
  onSubmit,
  onSkip,
}: CardProps) => {
  const gateId = ask.gate ?? '';
  const [gate, setGate] = useState(str(known(facts, gateId)));
  const [cells, setCells] = useState<Cells>(() => initialCells(ask, facts));
  const qid = useQids();
  const perDay = ask.unit === 'perDay';
  const rows = ask.rows ?? [];

  const toggle = (id: string) =>
    setCells((c) => ({
      ...c,
      [id]: { on: !c[id]?.on, count: c[id]?.count ?? '' },
    }));
  const setCount = (id: string, count: string) =>
    setCells((c) => ({ ...c, [id]: { on: true, count } }));

  const submit = () => {
    const values = gridValues(ask, gate, cells);
    if (gate !== 'Yes') return onSubmit(values, say(MANAGES, v));
    const lines = rows.flatMap((row) => {
      const on = HELP_KINDS.filter(
        (k) => row[k] && cells[row[k] as string]?.on,
      );
      if (!on.length) return [];
      const parts = on.map((k) => {
        const name = say(HELP_KIND[k], v).toLowerCase();
        if (!perDay) return name;
        const n = Number(values[row[k] as string]);
        return `${name} ${n === 1 ? 'once' : `${n} times`} a day`;
      });
      return [`${say(row.label, v)}: ${parts.join(', ')}`];
    });
    onSubmit(values, lines.join('. '));
  };

  return (
    <CardShell
      label={say(ask.title, v)}
      ready={gridReady(ask, gate, cells)}
      onContinue={submit}
      onSkip={onSkip}
    >
      <Toggle
        {...gateQuestion(ask, v)}
        hint={say(NEEDED_HINT, v)}
        value={gate}
        onChange={setGate}
      />
      {gate === 'Yes' && (
        <Stack spacing={1.5}>
          <Typography variant='body2'>{say(GRID.pick, v)}</Typography>
          <Stack spacing={2.5} divider={<Divider flexItem />}>
            {rows.map((row, r) => {
              const kinds = HELP_KINDS.filter((k) => row[k]);
              const rowLabel = say(row.label, v);
              return (
                <Stack key={r} spacing={1.25}>
                  <Typography id={qid(`row-${r}`)} variant='h6'>
                    {rowLabel}
                  </Typography>
                  <Chips
                    labelledBy={qid(`row-${r}`)}
                    options={kinds.map((k) => ({
                      value: row[k] as string,
                      label: say(HELP_KIND[k], v),
                    }))}
                    selected={kinds
                      .map((k) => row[k] as string)
                      .filter((id) => cells[id]?.on)}
                    onPick={toggle}
                  />
                  {perDay &&
                    kinds
                      .filter((k) => cells[row[k] as string]?.on)
                      .map((k) => {
                        const id = row[k] as string;
                        const kind = say(HELP_KIND[k], v);
                        return (
                          <Stack key={k} spacing={0.5}>
                            <Typography variant='body2'>{kind}</Typography>
                            <Count
                              label={GRID.timesLabel(kind, rowLabel)}
                              unit={GRID.timesADay}
                              value={cells[id]?.count ?? ''}
                              onChange={(count) => setCount(id, count)}
                              min={1}
                              max={24}
                            />
                          </Stack>
                        );
                      })}
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      )}
    </CardShell>
  );
};

HelpGridCard.displayName = 'HelpGridCard';
export default HelpGridCard;
